import React, { useContext, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CartContext } from "./CartContext";
import productData, { siteConfig } from "../Datas/productData";
import "../css/cartpage.scss";

const formatMoney = (value) =>
  new Intl.NumberFormat(siteConfig.currency.locale, {
    style: "currency",
    currency: siteConfig.currency.code,
  }).format(Number(value) || 0);

const CartPage = () => {
  const navigate = useNavigate();
  const {
    cartItems,
    cartCount,
    cartSubtotal,
    addToCart,
    removeFromCart,
    updateQuantity,
  } = useContext(CartContext);
  const [failedImages, setFailedImages] = useState({});
  const [cartNotice, setCartNotice] = useState("");

  const detailedItems = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const recommendations = useMemo(() => {
    const cartIds = new Set(cartItems.map((item) => item.id));
    const available = productData.filter(
      (product) => !cartIds.has(product.id) && Number(product.stock ?? 1) > 0
    );
    return [
      ...available.filter((product) => product.featured),
      ...available.filter((product) => !product.featured),
    ].slice(0, 4);
  }, [cartItems]);

  const derivedSubtotal = detailedItems.reduce(
    (total, item) => total + Number(item.product.price) * item.quantity,
    0
  );
  const subtotal = Number.isFinite(cartSubtotal)
    ? cartSubtotal
    : derivedSubtotal;
  const itemCount = Number.isFinite(cartCount)
    ? cartCount
    : detailedItems.reduce((total, item) => total + item.quantity, 0);
  const freeThreshold = Number(siteConfig.shipping.freeThreshold);
  const amountUntilFreeShipping = Math.max(0, freeThreshold - subtotal);
  const shippingCost = amountUntilFreeShipping === 0
    ? 0
    : Number(siteConfig.shipping.deliveryFee);
  const estimatedTotal = subtotal + shippingCost;
  const shippingProgress = Math.min(100, (subtotal / freeThreshold) * 100);

  const toTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const markImageAsFailed = (source) => {
    setFailedImages((current) => ({ ...current, [source]: true }));
  };

  const handleUpdateQuantity = (item, nextQuantity) => {
    const maximum = Math.max(1, Number(item.product.stock ?? 20));
    const safeQuantity = Math.min(Math.max(1, nextQuantity), maximum);
    updateQuantity(item.id, item.size, item.color, safeQuantity);
  };

  const handleCheckout = () => {
    if (!detailedItems.length) return;
    navigate("/checkout");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleQuickAdd = (product) => {
    const size = product.size?.[0] || "One size";
    const color = product.colors?.[0] || "Standard";
    addToCart(product.id, 1, size, color);
    setCartNotice(`${product.name} was added to your bag.`);
  };

  return (
    <main className="cart-page">
      <header className="cart-page__header">
        <p>{siteConfig.brandName} / Online boutique</p>
        <h1>Your bag</h1>
        <span>
          {itemCount} {itemCount === 1 ? "piece" : "pieces"}
        </span>
      </header>

      {detailedItems.length > 0 ? (
        <div className="cart-page__layout">
          <section className="cart-page__items" aria-label="Shopping bag items">
            <div className="shipping-progress" aria-live="polite">
              <div className="shipping-progress__copy">
                {amountUntilFreeShipping > 0 ? (
                  <span>
                    Add <strong>{formatMoney(amountUntilFreeShipping)}</strong> for
                    complimentary EU delivery.
                  </span>
                ) : (
                  <span>Complimentary EU delivery is unlocked.</span>
                )}
                <span>{Math.round(shippingProgress)}%</span>
              </div>
              <div className="shipping-progress__track" aria-hidden="true">
                <span style={{ width: `${shippingProgress}%` }} />
              </div>
            </div>

            <div className="cart-list">
              {detailedItems.map((item) => {
                const { product } = item;
                const image = product.image || product.images?.[0];
                const atMaximum = item.quantity >= Number(product.stock ?? 20);

                return (
                  <article
                    className="cart-line"
                    key={`${item.id}-${item.size}-${item.color}`}
                  >
                    <Link
                      className="cart-line__media"
                      to={`/shopDetails/${product.id}`}
                      onClick={toTop}
                      aria-label={`View ${product.name}`}
                    >
                      {image && !failedImages[image] ? (
                        <img
                          src={image}
                          alt={product.name}
                          loading="lazy"
                          onError={() => markImageAsFailed(image)}
                        />
                      ) : (
                        <span>{siteConfig.brandName}</span>
                      )}
                    </Link>

                    <div className="cart-line__details">
                      <p className="cart-line__category">{product.category}</p>
                      <h2>
                        <Link to={`/shopDetails/${product.id}`} onClick={toTop}>
                          {product.name}
                        </Link>
                      </h2>
                      <dl>
                        <div>
                          <dt>Colour</dt>
                          <dd>{item.color || "Standard"}</dd>
                        </div>
                        <div>
                          <dt>Size</dt>
                          <dd>{item.size || "One size"}</dd>
                        </div>
                      </dl>
                      <button
                        type="button"
                        className="cart-line__remove"
                        onClick={() =>
                          removeFromCart(item.id, item.size, item.color)
                        }
                      >
                        Remove
                      </button>
                    </div>

                    <div className="cart-line__commerce">
                      <p>{formatMoney(Number(product.price) * item.quantity)}</p>
                      <div className="cart-line__quantity" aria-label="Quantity selector">
                        <button
                          type="button"
                          onClick={() => handleUpdateQuantity(item, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          aria-label={`Decrease ${product.name} quantity`}
                        >
                          −
                        </button>
                        <output aria-live="polite">{item.quantity}</output>
                        <button
                          type="button"
                          onClick={() => handleUpdateQuantity(item, item.quantity + 1)}
                          disabled={atMaximum}
                          aria-label={`Increase ${product.name} quantity`}
                        >
                          +
                        </button>
                      </div>
                      <small>{formatMoney(product.price)} each</small>
                    </div>
                  </article>
                );
              })}
            </div>

            <Link className="cart-page__continue" to="/shop" onClick={toTop}>
              <span aria-hidden="true">←</span> Continue shopping
            </Link>
          </section>

          <aside className="cart-summary" aria-label="Order summary">
            <h2>Order summary</h2>
            <div className="cart-summary__row">
              <span>Subtotal</span>
              <span>{formatMoney(subtotal)}</span>
            </div>
            <div className="cart-summary__row">
              <span>Estimated delivery</span>
              <span>{shippingCost === 0 ? "Complimentary" : formatMoney(shippingCost)}</span>
            </div>
            <div className="cart-summary__total">
              <span>Estimated total</span>
              <strong>{formatMoney(estimatedTotal)}</strong>
            </div>
            <p className="cart-summary__note">
              Tax included. Final delivery options are confirmed at checkout.
            </p>
            <button type="button" onClick={handleCheckout}>
              Continue to checkout
            </button>
            <div className="cart-summary__services">
              <p>Secure demo checkout</p>
              <p>{siteConfig.shipping.returnsWindowDays}-day returns</p>
              <Link to="/shipping" onClick={toTop}>
                Delivery &amp; returns
              </Link>
            </div>
          </aside>
        </div>
      ) : (
        <section className="cart-page__empty">
          <p>Your selection is waiting to begin.</p>
          <h2>Your bag is empty</h2>
          <Link to="/shop" onClick={toTop}>
            Discover the collection
          </Link>
        </section>
      )}

      {recommendations.length > 0 && (
        <section className="cart-recommendations" aria-labelledby="recommendations-title">
          <div className="cart-recommendations__heading">
            <div>
              <p>Curated for you</p>
              <h2 id="recommendations-title">Complete the look</h2>
            </div>
            <Link to="/shop" onClick={toTop}>
              View all
            </Link>
          </div>

          <div className="cart-recommendations__grid">
            {recommendations.map((product) => {
              const image = product.image || product.images?.[0];
              return (
                <article className="recommendation-card" key={product.id}>
                  <Link
                    className="recommendation-card__media"
                    to={`/shopDetails/${product.id}`}
                    onClick={toTop}
                  >
                    {image && !failedImages[image] ? (
                      <img
                        src={image}
                        alt={product.name}
                        loading="lazy"
                        onError={() => markImageAsFailed(image)}
                      />
                    ) : (
                      <span>{siteConfig.brandName}</span>
                    )}
                  </Link>
                  <div className="recommendation-card__body">
                    <div>
                      <p>{product.category}</p>
                      <h3>
                        <Link to={`/shopDetails/${product.id}`} onClick={toTop}>
                          {product.name}
                        </Link>
                      </h3>
                      <span>{formatMoney(product.price)}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleQuickAdd(product)}
                      aria-label={`Quick add ${product.name}`}
                    >
                      +
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
          <p className="cart-recommendations__notice" aria-live="polite">
            {cartNotice}
          </p>
        </section>
      )}
    </main>
  );
};

export default CartPage;
